import React, { FC, memo, useMemo } from 'react';

import Typography from '@mui/material/Typography';
import Theme from '@src/theme';

import { formatBNWithCommas } from '@utils/formaters.utils';
import { getTokenListForUi } from '@utils/tokens.utils';

import ModalWrapper from '@components/common/Modal/ModalWrapper';
import TokensIcon from '@components/common/TokensIcon';
import FarmDetails from '@components/vaults/FarmDetails';
import ItemStats from '@components/vaults/ItemStats';
import TokenWeight from '@components/vaults/TokenWeight';

import { TokenSize } from '@constants/common/ui.constants';

import { NftModalProps } from '@app-types/common/modal.types';

import useStyles from './styles.module';

const NftModal: FC<NftModalProps> = ({ poolInfo, chainId, stakingToken }) => {
  const { classes } = useStyles();

  const {
    token0,
    tokenId,
    token1,
    tickIn,
    poolV3Fee,
    token0Weight,
    token1Weight,
    liquidity,
    unclaimedFees,
    amount0,
    amount0Usd,
    amount1,
    amount1Usd,
    feePendingRewards,
    lowPrice,
    upPrice,
    currentPrice,
  } = poolInfo;

  const liquidityStats = useMemo(
    () => [
      {
        token: token0,
        symbol: token0.symbol,
        amount: amount0,
        amountUsd: amount0Usd,
      },
      {
        token: token1,
        symbol: token1.symbol,
        amount: amount1,
        amountUsd: amount1Usd,
      },
    ],
    [amount0, token0, amount0Usd, amount1, token1, amount1Usd],
  );

  const feeStats = useMemo(
    () =>
      feePendingRewards.map((item) => ({
        token: item.rewardToken,
        symbol: item.rewardToken.symbol,
        amount: item.pendingRewards,
        amountUsd: item.pendingRewardsUsd,
      })),
    [feePendingRewards],
  );

  return (
    <ModalWrapper title={`#${tokenId}`}>
      <div className={classes.titleRow}>
        <FarmDetails
          chainId={chainId}
          stakingToken={stakingToken}
          isPoolV3PositionOutRange={!tickIn}
          farmTagsData={{ poolV3Fee }}
        />
        <div className={classes.rowGap}>
          <TokenWeight
            token0Weight={token0Weight}
            token1Weight={token1Weight}
            symbol0={token0?.symbol}
            symbol1={token1?.symbol}
          />
          <Typography
            variant="body2"
            color={(theme) => (tickIn ? theme.accent.a1 : theme.accent.a4)}
          >
            {tickIn ? 'In range' : 'Out range'}
          </Typography>
        </div>
      </div>
      <div className={classes.grid}>
        <div className={classes.column}>
          <ItemStats
            title="Liquidity"
            titleVariant="body2"
            titleColor={Theme.primary.p2}
            value={`$${formatBNWithCommas(liquidity, 2, 0, true)}`}
            valueVariant="subtitle2"
            valueColor={Theme.primary.p3}
            className={classes.stats}
            classNameValue={classes.statsValue}
          />
          <div className={classes.block}>
            {liquidityStats.map((item) => (
              <div className={classes.row} key={item.token.address}>
                <div className={classes.rowGap}>
                  <TokensIcon
                    tokens={getTokenListForUi(item.token)}
                    size={TokenSize.Small}
                  />
                  <Typography variant="body1">{item.symbol}</Typography>
                </div>
                <div className={classes.text}>
                  <Typography variant="body2">
                    {formatBNWithCommas(item.amount, 2, 0, true)}
                  </Typography>
                  <Typography
                    variant="caption1"
                    color={(theme) => theme.secondary.s3}
                  >
                    ${formatBNWithCommas(item.amountUsd, 2, 0, true)}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.column}>
          <ItemStats
            title="Unclaimed fees"
            titleVariant="body2"
            titleColor={Theme.primary.p2}
            value={`$${formatBNWithCommas(unclaimedFees, 2, 0, true)}`}
            valueVariant="subtitle2"
            valueColor={Theme.primary.p3}
            className={classes.stats}
            classNameValue={classes.statsValue}
          />
          <div className={classes.block}>
            {feeStats.map((item) => (
              <div className={classes.row} key={item.token.address}>
                <div className={classes.rowGap}>
                  <TokensIcon
                    tokens={getTokenListForUi(item.token)}
                    size={TokenSize.Small}
                  />
                  <Typography variant="body1">{item.symbol}</Typography>
                </div>
                <div className={classes.text}>
                  <Typography variant="body2">
                    {formatBNWithCommas(item.amount, 2, 0, true)}
                  </Typography>
                  <Typography
                    variant="caption1"
                    color={(theme) => theme.secondary.s3}
                  >
                    ${formatBNWithCommas(item.amountUsd, 2, 0, true)}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Typography variant="subtitle1" className={classes.title}>
        Price range
      </Typography>
      <div className={classes.grid}>
        <ItemStats
          title="Min Price"
          titleVariant="caption1"
          titleColor={Theme.secondary.s3}
          value={
            <div className={classes.columnText}>
              {formatBNWithCommas(lowPrice, 5, 0, false, true)}
              <Typography
                variant="caption1"
                color={(theme) => theme.secondary.s3}
              >
                {token1.symbol} per {token0.symbol}
              </Typography>
            </div>
          }
          valueVariant="subtitle1"
          valueColor={Theme.primary.p3}
        />
        <ItemStats
          title="Max Price"
          titleVariant="caption1"
          titleColor={Theme.secondary.s3}
          value={
            <div className={classes.columnText}>
              {formatBNWithCommas(upPrice, 5, 0, false, true)}
              <Typography
                variant="caption1"
                color={(theme) => theme.secondary.s3}
              >
                {token1.symbol} per {token0.symbol}
              </Typography>
            </div>
          }
          valueVariant="subtitle1"
          valueColor={Theme.primary.p3}
        />
      </div>
      <ItemStats
        title="Current Price"
        titleVariant="caption1"
        titleColor={Theme.secondary.s3}
        value={
          <div className={classes.columnText}>
            {formatBNWithCommas(currentPrice, 5, 0, false, true)}
            <Typography
              variant="caption1"
              color={(theme) => theme.secondary.s3}
            >
              {token1.symbol} per {token0.symbol}
            </Typography>
          </div>
        }
        valueVariant="subtitle1"
        valueColor={Theme.primary.p3}
        className={classes.margin}
      />
    </ModalWrapper>
  );
};

export default memo(NftModal);
